import { Divider, Menu, MenuItem } from '@material-ui/core';
import { ImportExport } from '@material-ui/icons';
import { Color, useUID } from '@superdispatch/ui';
import { ReactElement, useEffect } from 'react';
import { PostingSearchCriteriaDTO } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { MenuButton } from 'shared/ui/MenuButton';
import { ResponsiveTooltip } from 'shared/ui/ResponsiveTooltip';
import styled from 'styled-components';

type SortOption = (typeof sortGroupOptions)[number][number];
const sortGroupOptions = [
  ['posted_to_loadboard_at,desc', 'posted_to_loadboard_at,asc'],
  ['price_per_km,desc'],
  ['price,desc'],
  ['pickup.scheduled_at,asc', 'pickup.scheduled_at,desc'],
  ['pickup_distance,asc', 'delivery_distance,asc'],
] as const;

function formatSortField(value: SortOption): string {
  switch (value) {
    case 'posted_to_loadboard_at,desc':
      return 'Newest Loads';
    case 'posted_to_loadboard_at,asc':
      return 'Oldest Loads';
    case 'price_per_km,desc':
      return 'Highest Price per mi';
    case 'price,desc':
      return 'Highest Price';
    case 'pickup.scheduled_at,asc':
      return 'Earliest Available Pickup';
    case 'pickup.scheduled_at,desc':
      return 'Latest Available Pickup';
    case 'pickup_distance,asc':
      return 'Distance from Pickup';
    case 'delivery_distance,asc':
      return 'Distance from Delivery';
  }
}

function isSortOptionDisabled(
  sort: string,
  searchCriteria: PostingSearchCriteriaDTO,
): boolean {
  return (
    (sort === 'pickup_distance,asc' &&
      searchCriteria.pickup_venues.length === 0) ||
    (sort === 'delivery_distance,asc' &&
      searchCriteria.delivery_venues.length === 0)
  );
}

const MenuButtonStyled = styled(MenuButton)`
  width: 54px;
`;

interface Props {
  value: string | string[];
  onChange: (value: string) => void;
  searchCriteria: PostingSearchCriteriaDTO;
}

export function PostingListFilterSortButton({
  onChange,
  searchCriteria,
  value: valueProp,
}: Props) {
  const uid = useUID();
  const value = Array.isArray(valueProp) ? valueProp[0] : valueProp;

  useEffect(() => {
    if (value && isSortOptionDisabled(value, searchCriteria)) {
      onChange('posted_to_loadboard_at,desc');
    }
  }, [value, searchCriteria, onChange]);

  return (
    <MenuButtonStyled
      id={uid}
      aria-label="Sort options"
      renderMenu={({ onClose, ...menuProps }) => (
        <Menu {...menuProps} onClose={onClose}>
          {sortGroupOptions.reduce<ReactElement[]>((items, options, index) => {
            if (index !== 0) {
              items.push(<Divider key={index} />);
            }

            for (const option of options) {
              const isDisabled = isSortOptionDisabled(option, searchCriteria);
              items.push(
                <MenuItem
                  key={option}
                  value={option}
                  selected={option === value}
                  disabled={isDisabled}
                  button={isDisabled ? undefined : true}
                  onClick={() => {
                    if (isDisabled) return;

                    onChange(option);
                    onClose();
                  }}
                >
                  <ResponsiveTooltip
                    enterDelay={0}
                    title={
                      isDisabled
                        ? option === 'pickup_distance,asc'
                          ? 'Enter origin filter to enable sorting'
                          : 'Enter destination filter to enable sorting'
                        : ''
                    }
                  >
                    <span>{formatSortField(option)}</span>
                  </ResponsiveTooltip>
                </MenuItem>,
              );
            }
            return items;
          }, [])}
        </Menu>
      )}
    >
      <ImportExport htmlColor={Color.Dark100} />
    </MenuButtonStyled>
  );
}
